import React, { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { OrganizedTourPriceTraceTrigger } from "../../../../modules/organizedTours/components/OrganizedTourPriceTraceTrigger";
import {
  formatOrganizedTourSecondaryCurrencyDisplay,
  resolveOrganizedTourDualPrice,
} from "../../../../modules/organizedTours/utils/resolveOrganizedTourDualPrice";
import { shouldShowOrganizedTourPriceTraceUi } from "../../../../modules/organizedTours/utils/organizedTourTraceUiVisibility";
import { useSelectedResult } from "../../../../contexts/SelectedResultContext";

function formatPriceIls(value) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return `${Math.round(Number(value)).toLocaleString("he-IL")} ₪`;
}

function countTravelers(roomsAndPaxInfo) {
  if (!Array.isArray(roomsAndPaxInfo)) return null;
  let adults = 0;
  let children = 0;
  let infants = 0;
  roomsAndPaxInfo.forEach((room) => {
    adults += Array.isArray(room?.adults)
      ? room.adults.length
      : Number(room?.adults) || 0;
    children += Array.isArray(room?.kids)
      ? room.kids.length
      : Array.isArray(room?.children)
        ? room.children.length
        : Number(room?.children) || 0;
    infants += Array.isArray(room?.infants)
      ? room.infants.length
      : Number(room?.infants) || 0;
  });
  const total = adults + children + infants;
  if (total <= 0) return null;
  const parts = [];
  if (adults) parts.push(`${adults} מבוגרים`);
  if (children) parts.push(`${children} ילדים`);
  if (infants) parts.push(`${infants} תינוקות`);
  if (roomsAndPaxInfo.length > 1) {
    parts.push(`${roomsAndPaxInfo.length} חדרים`);
  }
  return parts.join(" · ");
}

export default function OrganizedTourProfileStickyBooking({
  viewModel,
  roomsAndPaxInfo,
  leftToPay,
  /** מחיר מההתאמת הצעה לאדמין (אם הוגדרה) — מוצג מתחת למחיר ההזמנה התקין */
  quotePriceIls = null,
  onContinue,
  continueLabel = "המשך לתשלום",
  showTermsHint = true,
  traceDeal = null,
  ldsOrderRowForTrace = null,
}) {
  const { currencyRate } = useSelectedResult();
  const vm = viewModel || {};
  const travelers = countTravelers(roomsAndPaxInfo);
  const displayPrice =
    leftToPay != null && Number.isFinite(Number(leftToPay))
      ? Number(leftToPay)
      : vm.priceIls;

  const displayQuote =
    quotePriceIls != null && Number.isFinite(Number(quotePriceIls))
      ? Number(quotePriceIls)
      : null;

  const dualPrice = useMemo(
    () => resolveOrganizedTourDualPrice(traceDeal || {}, displayPrice, currencyRate),
    [traceDeal, displayPrice, currencyRate]
  );

  const secondaryCurrencyDisplay = useMemo(
    () =>
      formatOrganizedTourSecondaryCurrencyDisplay(
        dualPrice.amount ?? dualPrice.usd,
        dualPrice.currency
      ),
    [dualPrice.amount, dualPrice.usd, dualPrice.currency]
  );

  const showPriceTrace = Boolean(traceDeal) && shouldShowOrganizedTourPriceTraceUi();

  useEffect(() => {
    if (!import.meta.env?.DEV) return;
    console.log("[ORG_TOUR_PROFILE_STICKY_BOOKING_RENDER]", {
      priceIls: displayPrice,
      quotePriceIls: displayQuote,
      travelers,
      secondaryAmount: dualPrice.amount ?? dualPrice.usd,
      secondaryCurrency: dualPrice.currency,
      secondarySource: dualPrice.source,
    });
  }, [
    displayPrice,
    displayQuote,
    travelers,
    dualPrice.amount,
    dualPrice.usd,
    dualPrice.currency,
    dualPrice.source,
  ]);

  const card = (
    <div className="otp-booking-card">
      <p className="otp-booking-card__label">סה״כ לתשלום</p>
      <p className="otp-booking-card__price" dir="rtl">
        <span className="otp-booking-card__price-main">
          <span>{formatPriceIls(displayPrice)}</span>
          {secondaryCurrencyDisplay ? (
            <span
              className="otp-booking-card__usd"
              dir="ltr"
              aria-label={
                dualPrice.currency === "EUR"
                  ? "מחיר משוער באירו"
                  : dualPrice.currency === "USD"
                    ? "מחיר משוער בדולר"
                    : "מחיר במטבע מקור"
              }
            >
              {secondaryCurrencyDisplay}
            </span>
          ) : null}
        </span>
      </p>
      {showPriceTrace ? (
        <OrganizedTourPriceTraceTrigger
          deal={traceDeal}
          context="organized_tours_profile_sticky"
          ldsOrderRow={ldsOrderRowForTrace}
          displayIls={displayPrice}
        />
      ) : null}
      {displayQuote != null && Math.round(displayQuote) !== Math.round(displayPrice) ? (
        <p className="otp-booking-card__pax otp-booking-card__hint">
          הצעת מחיר ללקוח: {formatPriceIls(displayQuote)}
        </p>
      ) : null}
      {travelers ? (
        <p className="otp-booking-card__pax">{travelers}</p>
      ) : null}
      {vm.depDateLabel ? (
        <p className="otp-booking-card__dates">
          {vm.depDateLabel}
          {vm.retDateLabel && vm.retDateLabel !== "—" ? ` – ${vm.retDateLabel}` : ""}
        </p>
      ) : null}
      {showTermsHint ? (
        <p className="otp-booking-card__hint">
          המשך בתחתית העמוד לאחר מילוי נוסעים ואישור תנאים
        </p>
      ) : null}
      {typeof onContinue === "function" ? (
        <button type="button" className="otp-booking-card__cta" onClick={onContinue}>
          {continueLabel}
        </button>
      ) : null}
    </div>
  );

  return (
    <>
      <aside className="otp-booking-rail" aria-label="סיכום הזמנה">
        {card}
      </aside>
      <motion.div
        className="otp-booking-mobile-bar"
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="otp-booking-mobile-bar__price" dir="rtl">
          <span className="otp-booking-mobile-bar__price-main">{formatPriceIls(displayPrice)}</span>
          {secondaryCurrencyDisplay ? (
            <span
              className="otp-booking-mobile-bar__usd"
              dir="ltr"
              aria-label={
                dualPrice.currency === "EUR"
                  ? "מחיר משוער באירו"
                  : dualPrice.currency === "USD"
                    ? "מחיר משוער בדולר"
                    : "מחיר במטבע מקור"
              }
            >
              {secondaryCurrencyDisplay}
            </span>
          ) : null}
          {showPriceTrace ? (
            <OrganizedTourPriceTraceTrigger
              deal={traceDeal}
              context="organized_tours_profile_mobile_bar"
              ldsOrderRow={ldsOrderRowForTrace}
              displayIls={displayPrice}
              variant="icon"
            />
          ) : null}
        </div>
        {typeof onContinue === "function" ? (
          <button type="button" className="otp-booking-mobile-bar__cta" onClick={onContinue}>
            {continueLabel}
          </button>
        ) : null}
      </motion.div>
    </>
  );
}
